'use client';
import type { ReactNode, RefObject } from 'react';
import { useRef } from 'react';
import { createPortal } from 'react-dom';

import { BackdropStyled } from '@/app/components/overlays/backdrop';
import { useScrollLock } from '@/app/components/overlays/hooks/useScrollLock';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';

interface ClientPortalProps {
  children: ReactNode;
  show?: boolean;
  onClose?: () => void;
  targetRef?: RefObject<HTMLElement | null>;
}

const ClientPortal = ({
  children,
  targetRef,
  show = false,
}: ClientPortalProps) => {
  return show
    ? createPortal(children, targetRef?.current ?? document.documentElement)
    : null;
};

export interface LoadingOverlayProps {
  show?: boolean;
  targetRef?: ClientPortalProps['targetRef'];
  children: ReactNode;
}

export const LoadingOverlay = ({
  show = false,
  targetRef,
  children,
}: LoadingOverlayProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { isTransitioning, className } = useMountTransition({
    ref,
    isMount: show,
    baseClassName: 'opacity-0 transition-opacity duration-300 fixed inset-0',
    animateClassName: 'opacity-100',
  });

  useScrollLock({
    overlayKey: 'modal-loading',
    enabled: isTransitioning,
  });

  if (!isTransitioning) {
    return null;
  }

  return (
    <ClientPortal show={isTransitioning} targetRef={targetRef}>
      <div
        ref={ref}
        className={BackdropStyled({
          className: cn(className, 'z-50'),
        })}
        role="dialog"
        aria-modal="true"
        aria-label="Loading"
        tabIndex={-1}
      >
        <div
          className="absolute inset-0 flex items-center justify-center"
          role="status"
          aria-live="polite"
        >
          {children}
        </div>
      </div>
    </ClientPortal>
  );
};
